// @ts-check
// Catálogo de blocos do editor de bots (contrato de bots, seções 2.1, 2.3 e 22.2): categorias na ordem das sanfonas,
// cor por categoria (os mesmos tokens das categorias dos fluxos, claro e escuro) e um item por bloco, com a
// configuração inicial de cada um (seção 4). A mensagem tem três entradas (texto, botões e lista) e o responsável
// duas (fixo e rodízio). O catálogo compartilhado `shared/bots/bot-catalog.js` é da frente do motor; a tela usa este
// até a junção, com as mesmas categorias e cores.
import { FLOW_CATEGORY_COLORS } from '../../../shared/flows/flow-catalog.js';
import { defaultNodeConfig as defaultFlowNodeConfig } from '../../../shared/flows/flow-definition.js';
import { BOT_NODE_CATEGORIES } from '../../../shared/bots/bot-definition.js';
import { BOT_NODE_ICONS } from '../../../shared/bots/bot-summary.js';
import { isBotSplitType, isBotTerminalType } from './bot-tree.js';

/**
 * @typedef {import('../../../shared/bots/bot-definition.js').BotNodeType} BotNodeType
 * @typedef {import('../../../shared/bots/bot-definition.js').BotNodeCategory} BotNodeCategory
 * @typedef {(key: string, params?: Record<string, unknown>) => string} Translate
 * @typedef {{ key: string, type: BotNodeType, category: BotNodeCategory, icon: string, nameKey: string, isSplit: boolean, isTerminal: boolean,
 *   buildConfig: (generateId: () => string, translate: Translate) => Record<string, any> }} BotCatalogItem
 */

export const BOT_CATALOG_CATEGORIES = Object.freeze(/** @type {BotNodeCategory[]} */ (['MESSAGES', 'PATH', 'IDENTITY', 'SERVICE', 'CRM', 'INTEGRATIONS']));

/** Cor de cada categoria, pelos tokens das categorias dos fluxos. */
export const BOT_CATEGORY_COLORS = Object.freeze(/** @type {Record<BotNodeCategory, { background: string, icon: string }>} */ ({
  MESSAGES: FLOW_CATEGORY_COLORS.COMMUNICATION,
  PATH: FLOW_CATEGORY_COLORS.PATH,
  IDENTITY: FLOW_CATEGORY_COLORS.WAIT,
  SERVICE: FLOW_CATEGORY_COLORS.OWNER,
  CRM: FLOW_CATEGORY_COLORS.LEAD,
  INTEGRATIONS: FLOW_CATEGORY_COLORS.INTEGRATIONS,
}));


/** @param {() => string} generateId @param {Translate} translate @param {'BUTTONS' | 'LIST'} presentation */
function staticOptions(generateId, translate, presentation) {
  const titles = presentation === 'BUTTONS'
    ? [translate('bots.catalog.defaults.yes'), translate('bots.catalog.defaults.no')]
    : [1, 2, 3].map((number) => translate('bots.catalog.defaults.option', { number }));
  return {
    source: 'STATIC', presentation, listButtonLabel: presentation === 'LIST' ? translate('bots.catalog.defaults.listButton') : null, header: null, footer: null,
    items: titles.map((title) => ({ id: generateId(), title, description: null })), segmentVariable: null, saveTo: null, addHumanOption: false,
    retries: null, timeoutMinutes: null,
  };
}

/**
 * Configuração inicial de um bloco do tipo (seção 4); textos de exemplo pelo catálogo de textos.
 * @param {BotNodeType} type @param {() => string} generateId @param {Translate} translate
 * @returns {Record<string, any>}
 */
export function defaultBotConfig(type, generateId, translate) {
  switch (type) {
    case 'SEND_MESSAGE': return { text: '', media: null, options: null };
    case 'ASK': return { text: '', inputKind: 'TEXT', validation: null, saveTo: { variable: '', target: null, fieldName: null }, retries: null, retryText: null, timeoutMinutes: null, isSensitive: false };
    case 'CONDITION': return { logic: 'AND', rules: [] };
    case 'SPLIT_PERCENT': return { shares: { a: 50, b: 50 } };
    case 'SET_VARIABLE': return { name: '', value: '' };
    case 'GOTO': return { targetNodeId: null };
    case 'SUBBOT': return { botId: null, passVariables: [] };
    case 'END': return { text: null };
    case 'IDENTIFY_GUARDIAN': return { allowWeakData: true, askSpeakerWhenShared: true, texts: { askWho: null, askCpf: null, askBirthDate: null, notFound: null } };
    case 'CHOOSE_STUDENT': return { allowWholeFamily: true, text: null, topic: 'GENERAL' };
    case 'STRONG_VERIFICATION': return { purpose: 'SENSITIVE_TOPIC', texts: { sent: null, askCode: null, invalid: null } };
    case 'SET_PURPOSE': return { purpose: 'SERVICE', teamId: null };
    case 'OPEN_TICKET': return { teamId: null, category: null, priority: null, subjectTemplate: '', summaryTemplate: null, ticketType: 'REQUEST' };
    case 'HANDOFF': return { target: { kind: 'TEAM', teamId: null }, openText: null, closedText: null, summaryTemplate: null, priority: null, category: null };
    case 'CREATE_CAPTURE_CARD':
      return { studentNameVariable: null, courseVariable: null, academicPeriodVariable: null, campusVariable: null, leadSourceVariable: null, assign: 'ROUND_ROBIN', workspaceMemberIds: [] };
    case 'UPDATE_STAGE': return { stage: null, lostReason: null, notLeadReason: null };
    case 'UPDATE_FIELD': return { target: 'CARD', fieldName: null, value: '' };
    case 'ADD_TAGS':
    case 'REMOVE_TAGS': return { tags: [] };
    case 'ASSIGN_OWNER': return { strategy: 'SPECIFIC', workspaceMemberIds: [], onlyIfEmpty: false };
    case 'CREATE_TASK': return defaultFlowNodeConfig('CREATE_TASK');
    case 'ADD_NOTE': return { body: '', target: 'CARD' };
    case 'SEND_NOTIFICATION': return defaultFlowNodeConfig('SEND_NOTIFICATION');
    case 'SCHEDULE_VISIT':
      return { bookingPageId: null, text: translate('bots.catalog.defaults.visitText'), confirmText: translate('bots.catalog.defaults.visitConfirmText'), timeoutMinutes: null };
    case 'CALL_WEBHOOK': return { ...defaultFlowNodeConfig('CALL_WEBHOOK'), saveResponseTo: null };
    case 'HTTP_REQUEST': return { ...defaultFlowNodeConfig('HTTP_REQUEST'), saveResponseTo: null };
    default: return {};
  }
}

/**
 * @param {string} key @param {BotNodeType} type @param {{ nameKey?: string, buildConfig?: BotCatalogItem['buildConfig'], isSplit?: boolean }} [extra]
 * @returns {BotCatalogItem}
 */
function item(key, type, extra = {}) {
  return Object.freeze({
    key, type, category: BOT_NODE_CATEGORIES[type], icon: BOT_NODE_ICONS[type], nameKey: extra.nameKey ?? `bots.nodes.${type}.name`,
    isSplit: extra.isSplit ?? isBotSplitType(type), isTerminal: isBotTerminalType(type),
    buildConfig: extra.buildConfig ?? ((generateId, translate) => defaultBotConfig(type, generateId, translate)),
  });
}

/** @type {ReadonlyArray<BotCatalogItem>} */
export const BOT_CATALOG_ITEMS = Object.freeze([
  item('SEND_MESSAGE', 'SEND_MESSAGE'),
  item('MESSAGE_BUTTONS', 'SEND_MESSAGE', {
    nameKey: 'bots.catalog.items.MESSAGE_BUTTONS', isSplit: true,
    buildConfig: (generateId, translate) => ({ ...defaultBotConfig('SEND_MESSAGE', generateId, translate), options: staticOptions(generateId, translate, 'BUTTONS') }),
  }),
  item('MESSAGE_LIST', 'SEND_MESSAGE', {
    nameKey: 'bots.catalog.items.MESSAGE_LIST', isSplit: true,
    buildConfig: (generateId, translate) => ({ ...defaultBotConfig('SEND_MESSAGE', generateId, translate), options: staticOptions(generateId, translate, 'LIST') }),
  }),
  item('ASK', 'ASK'),
  item('CONDITION', 'CONDITION'),
  item('SPLIT_PERCENT', 'SPLIT_PERCENT'),
  item('SET_VARIABLE', 'SET_VARIABLE'),
  item('GOTO', 'GOTO'),
  item('SUBBOT', 'SUBBOT'),
  item('END', 'END'),
  item('IDENTIFY_GUARDIAN', 'IDENTIFY_GUARDIAN'),
  item('CHOOSE_STUDENT', 'CHOOSE_STUDENT'),
  item('STRONG_VERIFICATION', 'STRONG_VERIFICATION'),
  item('SET_PURPOSE', 'SET_PURPOSE'),
  item('OPEN_TICKET', 'OPEN_TICKET'),
  item('HANDOFF', 'HANDOFF'),
  item('CREATE_CAPTURE_CARD', 'CREATE_CAPTURE_CARD'),
  item('SCHEDULE_VISIT', 'SCHEDULE_VISIT'),
  item('UPDATE_STAGE', 'UPDATE_STAGE'),
  item('UPDATE_FIELD', 'UPDATE_FIELD'),
  item('ADD_TAGS', 'ADD_TAGS'),
  item('REMOVE_TAGS', 'REMOVE_TAGS'),
  item('ASSIGN_OWNER', 'ASSIGN_OWNER'),
  item('DISTRIBUTE_OWNERS', 'ASSIGN_OWNER', {
    nameKey: 'bots.catalog.items.DISTRIBUTE_OWNERS',
    buildConfig: (generateId, translate) => ({ ...defaultBotConfig('ASSIGN_OWNER', generateId, translate), strategy: 'ROUND_ROBIN' }),
  }),
  item('CREATE_TASK', 'CREATE_TASK'),
  item('ADD_NOTE', 'ADD_NOTE'),
  item('SEND_NOTIFICATION', 'SEND_NOTIFICATION'),
  item('CALL_WEBHOOK', 'CALL_WEBHOOK'),
  item('HTTP_REQUEST', 'HTTP_REQUEST'),
]);

const ITEMS_BY_KEY = new Map(BOT_CATALOG_ITEMS.map((entry) => [entry.key, entry]));

/** @param {string} key */
export const botCatalogItem = (key) => ITEMS_BY_KEY.get(key) ?? null;

/** Variáveis CSS da cor da categoria do bloco. @param {string} type */
export function botCategoryStyle(type) {
  const category = BOT_NODE_CATEGORIES[/** @type {BotNodeType} */ (type)];
  const colors = category ? BOT_CATEGORY_COLORS[category] : null;
  return colors ? { '--flow-category-color': `var(${colors.background})`, '--flow-category-icon-color': `var(${colors.icon})` } : {};
}
